import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useBoundStore } from "Zustand/store/mainStore";
import { useNavigate } from "react-router";
import { Button, Select, Typography } from "Atoms";
import {
  // createTestPlanAPI,
  createTestProjectAPI,
  createTestSpecificationAPI,
  getCustomerListAPI,
  getKpisAPI,
  getTestSpecificationsAPI,
  getTestPlansAPI,
  getTestvisionUserRolesAPI,
} from "Zustand/services/projectsService";
import {
  Add,
  AdminPanelSettings,
  Badge as BadgeIcon,
  Groups,
  Logout,
  Person,
  QueryStats,
} from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

// import Logo from "Assets/svgs/logo.svg"; // replace path as needed
import Logo from "Assets/images/logo.png";
import CreateEditEntityModal from "./CreateEditEntityModal";
import { namespace } from "Zustand/constants";
import { normalizeUserRoles } from "Utils/helperFunctions";

type CustomerOption = {
  label: string;
  value: string;
};

const normalizeCustomerOptions = (response: any): CustomerOption[] => {
  const source =
    (Array.isArray(response) && response) ||
    response?.customers ||
    response?.customerlist ||
    response?.customerList ||
    response?.data ||
    [];

  if (!Array.isArray(source)) return [];

  return source
    .map((item: any) => {
      const value =
        typeof item === "string"
          ? item
          : item?.customer ||
            item?.customerName ||
            item?.customername ||
            item?.name ||
            item?.id ||
            "";

      const label =
        typeof item === "string"
          ? item
          : item?.customerName ||
            item?.customername ||
            item?.name ||
            item?.customer ||
            value;

      return value ? { label: String(label), value: String(value) } : null;
    })
    .filter(Boolean) as CustomerOption[];
};

const Projects = () => {
  const {
    getProjects,
    projects,
    loading,
    error,
    legalEntityNodeid,
    uploadAttachments,
    isUploading,
    setIsUploading,
    logout,
    testReports,
    getTestReports,
    resetReportsState,
    testvisionusername,
    userRoles: loginUserRoles,
  } = useBoundStore();

  const { accounts } = useMsal();
  const msalUsername = accounts?.[0]?.username || accounts?.[0]?.name || "";

  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(false);

  const [kpisOpen, setKpisOpen] = useState(false);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [kpis, setKpis] = useState({
    testspecifications: 0,
    approvedtestplans: 0,
    approvedtestreports: 0,
  });

  const [open, setOpen] = useState(false); // ← modal flag
  const [newProjectAttachments, setNewProjectAttachments] = useState<any>([]);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [openTestSpecificationModal, setOpenTestSpecificationModal] =
    useState(false);
  const [newTestSpecificationAttachments, setNewTestSpecificationAttachments] =
    useState<any>([]);

  const [selectedProject, setSelectedProject] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [testSpecifications, setTestSpecifications] = useState<any[]>([]);
  const [selectedTestSpecification, setSelectedTestSpecification] = useState("");

  const [selectedTestReport, setSelectedTestReport] = useState("");
  
  const navigate = useNavigate();

  // Reuse the visible list data to prevent duplicate names before create calls.
  const existingProjectNames = useMemo(
    () =>
      (projects || [])
        .map((project: any) => project?.testproject?.name || project?.name || "")
        .filter(Boolean),
    [projects]
  );

  const existingTestSpecificationNames = useMemo(
    () =>
      (testSpecifications || [])
        .map(
          (specification: any) =>
            specification?.testspecification?.name || specification?.name || ""
        )
        .filter(Boolean),
    [testSpecifications]
  );

  const loadTestSpecifications = async () => {
    if (!legalEntityNodeid) {
      setTestSpecifications([]);
      return;
    }

    try {
      const specifications = await getTestSpecificationsAPI(legalEntityNodeid);
      setTestSpecifications(specifications);
    } catch (err) {
      console.error("Failed to load test specifications", err);
    }
  };

  const loadCustomerOptions = async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await getCustomerListAPI();
      setCustomerOptions(normalizeCustomerOptions(response));
    } catch (err) {
      console.error("Failed to load customers", err);
      setCustomerOptions([]);
      toast.error("Failed to load customers");
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleProjectSelect = async (event: any) => {
    const value = event.target.value;
    setSelectedProject(value);
    setSelectedPlan(""); // reset plan on project change
    setSelectedTestReport("");
    setSelectedPlan("");

    // Fetch plans for selected project
    const plansData: any = await getTestPlansAPI(value);
    setPlans(plansData || []);

    resetReportsState();
  };

  const handlePlanSelect = (event: any) => {
    setSelectedPlan(event.target.value);
  };

  const handleTestReportSelect = (event: any) => {
    setSelectedTestReport(event.target.value);
  };

  const handleTestSpecificationSelect = (event: any) => {
    setSelectedTestSpecification(event.target.value);
  };

  const handleOpenKpis = async () => {
    setKpisOpen(true);
    setLoadingKpis(true);
    try {
      const response = await getKpisAPI();
      setKpis({
        testspecifications: Number(response?.testspecifications) || 0,
        approvedtestplans: Number(response?.approvedtestplans) || 0,
        approvedtestreports: Number(response?.approvedtestreports) || 0,
      });
    } catch (err) {
      // The /kpis route doesn't exist yet on the backend — show zeros
      // instead of leaving the modal stuck or erroring out.
      console.error("Failed to load KPIs", err);
      setKpis({
        testspecifications: 0,
        approvedtestplans: 0,
        approvedtestreports: 0,
      });
    } finally {
      setLoadingKpis(false);
    }
  };

  const handleTestSpecificationOpen = () => {
    const specification =
      testSpecifications.find((item: any) => {
        const nodeId =
          item?.testspecification?.nodeid || item?.nodeid || "";
        return nodeId === selectedTestSpecification;
      }) || null;

    navigate("/testspecification-detail", {
      state: {
        testspecificationnodeid: selectedTestSpecification,
        testSpecification: specification,
      },
    });
  };

  const handleProjectOpen = async (hasPlan: any) => {
    navigate("/project-detail", {
      state: {
        projectnodeid: selectedProject,
        project: projects.length
          ? projects.filter((project: any) => {
              return project?.testproject.nodeid === selectedProject;
            })[0]
          : {},
        plannodeid: selectedPlan,
        hasPlan: hasPlan,
      },
    });
  };

  const handleTestReportOpen = async (hasPlan: any) => {
    navigate("/report-detail", {
      state: {
        reportnodeid: selectedTestReport,
        project: projects.length
          ? projects.filter((project: any) => {
              return project?.testproject.nodeid === selectedProject;
            })[0]
          : {},
        plannodeid: selectedPlan,
      },
    });
  };

  /* ➋ create‑project save handler */
  const handleCreateProject = async ({
    projectName,
    projectDesc,
    attachments,
    customer,
  }: {
    projectName: string;
    projectDesc: string;
    attachments: any[];
    customer?: string;
  }) => {
    try {
      let newProjectIdentifier = uuidv4();
      const payload: any = {
        identifier: newProjectIdentifier, // or a custom slug
        name: projectName,
        description: projectDesc,
        legalentitynodeid: legalEntityNodeid,
        customer,
      };

      await createTestProjectAPI(payload);

      /* 2️⃣  Immediately create an initial test‑plan */
      // await createTestPlanAPI({
      //   identifier: uuidv4(),
      //   version: "1.0.0",
      //   testprojectnodeid: namespace + newProjectIdentifier,
      // });

      /* optional – upload attachments now that we have the project nodeid */
      if (attachments.length) {
        setIsUploading(true);
        await uploadAttachments(
          namespace + "/" + newProjectIdentifier,
          attachments,
          []
        );
        setIsUploading(false);
      }

      // refresh list & close modal
      await getProjects(legalEntityNodeid || "");
      setOpen(false);
    } catch (err) {
      console.error("Project creation failed", err);
      // toast / snackbar here
    }
  };

  const handleCreateTestSpecification = async ({
    projectName,
    projectDesc,
    attachments,
  }: {
    projectName: string;
    projectDesc: string;
    attachments: any[];
  }) => {
    try {
      if (!legalEntityNodeid) {
        toast.error("Legal entity is missing");
        return;
      }

      const identifier = uuidv4();
      const payload = {
        identifier,
        name: projectName,
        description: projectDesc,
        legalentitynodeid: legalEntityNodeid,
      };

      const response: any = await createTestSpecificationAPI(payload);
      const createdNodeId =
        response?.testspecification?.nodeid ??
        response?.nodeid ??
        `${namespace}/${identifier}`;

      if (attachments.length) {
        setIsUploading(true);
        await uploadAttachments(createdNodeId, attachments, []);
        setIsUploading(false);
      }

      await loadTestSpecifications();
      setOpenTestSpecificationModal(false);
      setNewTestSpecificationAttachments([]);
      toast.success("Test specification created");
    } catch (err) {
      console.error("Create test specification failed", err);
      setIsUploading(false);
      toast.error("Failed to create test specification");
    }
  };

  useEffect(() => {
    let legalentityNodeId = legalEntityNodeid || "";
    getProjects(legalentityNodeId);
  }, [legalEntityNodeid, getProjects]);

  useEffect(() => {
    const loadUserRoles = async () => {
      if (!testvisionusername) {
        setUserRoles([]);
        setSelectedRole("");
        return;
      }

      // Login already fetched & validated these roles (authStore.login /
      // loginWithSso) — reuse them instead of hitting the API again. Only
      // re-fetch if they aren't cached, e.g. after a hard refresh.
      if (loginUserRoles.length > 0) {
        setUserRoles(loginUserRoles);
        setSelectedRole("");
        return;
      }

      setLoadingRoles(true);
      try {
        const response = await getTestvisionUserRolesAPI(testvisionusername);
        setUserRoles(normalizeUserRoles(response));
        setSelectedRole("");
      } catch (err) {
        console.error("Failed to load user roles", err);
        setUserRoles([]);
        setSelectedRole("");
      } finally {
        setLoadingRoles(false);
      }
    };

    loadUserRoles();
  }, [testvisionusername, loginUserRoles]);

  useEffect(() => {
    loadTestSpecifications();
  }, [legalEntityNodeid]);

  useEffect(() => {
    if (selectedPlan) getTestReports(selectedPlan);
  }, [selectedPlan]);

  useEffect(() => {
    if (open) {
      loadCustomerOptions();
    }
  }, [open]);


  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          py: 1.5,
          bgcolor: "background.paper",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          boxShadow: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Person fontSize="small" color="action" />
          <Typography variant="body2">
            <strong>User:</strong> {msalUsername || "-"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BadgeIcon fontSize="small" color="action" />
          <Typography variant="body2">
            <strong>TestVision User:</strong> {testvisionusername || "-"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 200 }}>
          <Groups fontSize="small" color="action" />
          {loadingRoles ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading roles...
              </Typography>
            </Box>
          ) : userRoles.length > 0 ? (
            <Select
              label="Role"
              value={selectedRole}
              onChange={(e: any) => setSelectedRole(e.target.value)}
              options={userRoles.map((role) => ({ label: role, value: role }))}
              size="small"
              sx={{ flex: 1 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No roles found
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<QueryStats fontSize="small" />}
          onClick={handleOpenKpis}
        >
          KPIs
        </Button>
      </Box>

      <Container
        maxWidth="sm"
        sx={{ flex: 1, display: "flex", alignItems: "center" }}
      >
      <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
        {loading && <CircularProgress />}

        <Box
          mb={3}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* <Typography variant="h5" sx={{ margin: "0 auto" }}>
                Welcome
              </Typography> */}

          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate("/admin/users");
            }}
            startIcon={<AdminPanelSettings />}
          >
            Admin
          </Button>

          <img src={Logo} alt="Logo" style={{ height: 42, margin: "0 auto" }} />

          <Button
            variant="outlined"
            color="error"
            onClick={logout}
            startIcon={<Logout />}
          >
            Logout
          </Button>
        </Box>

        {error && <Typography color="error">Error: {error}</Typography>}

        <br />
        <br />

        {testSpecifications.length > 0 ? (
          <Select
            label="Select Test Specification"
            value={selectedTestSpecification}
            onChange={handleTestSpecificationSelect}
            options={testSpecifications.map((specification: any) => ({
              label:
                specification?.testspecification?.name ||
                specification?.name ||
                specification?.testspecification?.nodeid ||
                "Unnamed Test Specification",
              value:
                specification?.testspecification?.nodeid ||
                specification?.nodeid ||
                "",
            }))}
            autoWidth={false}
            multiple={false}
            native={false}
          />
        ) : (
          <div>
            No test specifications found. Please create a new test specification.
            <br />
            <br />
          </div>
        )}

        <Box display="flex" justifyContent="space-between" paddingTop={"3%"} pb={2}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setOpenTestSpecificationModal(true)}
          >
            New
          </Button>

          <Button
            variant="contained"
            disabled={!selectedTestSpecification}
            onClick={handleTestSpecificationOpen}
          >
            Open
          </Button>
        </Box>

        {projects && projects.length > 0 ? (
          <>
            <Select
              label="Select Project"
              value={selectedProject}
              onChange={handleProjectSelect}
              options={projects.map((project: any) => ({
                label: project?.testproject?.name,
                value: project?.testproject?.nodeid,
              }))}
              autoWidth={false}
              multiple={false}
              native={false}
            />
          </>
        ) : (
          <div>
            {" "}
            No projects found. Please create a new project. <br /> <br />{" "}
          </div>
        )}

        <Box display="flex" justifyContent="space-between" paddingTop={"3%"}>
          <Button
            variant="outlined"
            onClick={() => setOpen(true)}
            startIcon={<Add />}
          >
            New
          </Button>

          {projects && projects.length > 0 && (
            <Button
              variant="contained"
              onClick={() => {
                handleProjectOpen(false);
              }}
              disabled={!selectedProject}
            >
              Open
            </Button>
          )}
        </Box>

        {plans.length > 0 && (
          <>
            <br />
            <Select
              label="Select Plan"
              value={selectedPlan}
              onChange={handlePlanSelect}
              options={plans.map((plan: any) => ({
                label: plan?.testplan?.name + "-" + plan?.testplan?.phase + "-" + plan?.testplan?.version || plan?.testplan?.nodeid,
                value: plan?.testplan?.nodeid,
              }))}
              autoWidth={false}
              multiple={false}
              native={false}
            />
          </>
        )}

        <div
          style={{ display: "flex", justifyContent: "end", paddingTop: "3%" }}
        >
          {selectedPlan.length > 0 && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  handleProjectOpen(true);
                }}
                disabled={!selectedPlan}
              >
                Open
              </Button>
            </>
          )}
        </div>


        {selectedPlan && testReports.length > 0 && (
          <>
            <br />
            <Select
              label="Select Test Report"
              value={selectedTestReport}
              onChange={handleTestReportSelect}
              options={testReports.map((plan: any) => ({
                label: plan?.testreport?.id || plan?.testreport?.nodeid,
                value: plan?.testreport?.nodeid,
              }))}
              autoWidth={false}
              multiple={false}
              native={false}
            />
          </>
        )}

        <div
          style={{ display: "flex", justifyContent: "end", paddingTop: "3%" }}
        >
          {selectedTestReport.length > 0 && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  handleTestReportOpen(true);
                }}
                disabled={!selectedTestReport}
              >
                Open
              </Button>
            </>
          )}
        </div>

        <CreateEditEntityModal
          open={open}
          onClose={() => setOpen(false)}
          onSave={handleCreateProject}
          attachments={newProjectAttachments}
          setAttachments={setNewProjectAttachments}
          isUploading={isUploading}
          isModel={true}
          showCustomerSelect={true}
          customerLabel="Customer"
          customerOptions={customerOptions}
          customerRequired={true}
          isLoadingCustomers={isLoadingCustomers}
          existingNames={existingProjectNames}
          duplicateNameMessage="A project with this name already exists."
        />

        <CreateEditEntityModal
          open={openTestSpecificationModal}
          onClose={() => setOpenTestSpecificationModal(false)}
          onSave={handleCreateTestSpecification}
          attachments={newTestSpecificationAttachments}
          setAttachments={setNewTestSpecificationAttachments}
          isUploading={isUploading}
          isModel={true}
          dialogTitle="Create Test Specifications"
          nameLabel="Test Specification Name"
          descriptionLabel="Test Specification Description"
          allowedFileExtensions={["pdf"]}
          existingNames={existingTestSpecificationNames}
          duplicateNameMessage="A test specification with this name already exists."
        />

        <Box textAlign="center" mt={4}></Box>
      </Paper>
      </Container>

      <Dialog
        open={kpisOpen}
        onClose={() => setKpisOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
      >
        <DialogTitle>KPIs</DialogTitle>
        <DialogContent dividers>
          {loadingKpis ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Test specifications</Typography>
                <Typography fontWeight="bold">{kpis.testspecifications}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Approved test plans</Typography>
                <Typography fontWeight="bold">{kpis.approvedtestplans}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Approved test reports</Typography>
                <Typography fontWeight="bold">{kpis.approvedtestreports}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setKpisOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
